import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Banknote,
  Boxes,
  Clock3,
  PackageCheck,
  ShieldAlert,
  Sparkles,
  TrendingUp,
  Zap,
} from "lucide-react";
import { callBackend } from "../utils/api";
import { useNotify } from "./Notification";

const formatNumber = (value) => Math.abs(value).toLocaleString();

const formatAwaySince = (value) => {
  if (!value) return "Earlier";
  return new Date(value).toLocaleString();
};

export const formatAwayDuration = (seconds) => {
  const totalMinutes = Math.max(0, Math.floor((Number(seconds) || 0) / 60));
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  const parts = [];

  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);

  return parts.join(" ");
};

const useCountUp = (value) => {
  const target = Math.max(0, Math.abs(Number(value) || 0));
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduceMotion || target === 0) {
      setDisplayValue(target);
      return undefined;
    }

    let animationFrame;
    let startedAt;
    const duration = 550;
    const animate = (timestamp) => {
      if (startedAt === undefined) startedAt = timestamp;
      const progress = Math.min(1, (timestamp - startedAt) / duration);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.round(target * easedProgress));
      if (progress < 1) animationFrame = requestAnimationFrame(animate);
    };

    setDisplayValue(0);
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [target]);

  return displayValue;
};

const AnimatedNumber = ({ value, prefix = "", suffix = "" }) => {
  const displayValue = useCountUp(value);
  return (
    <>
      {prefix}
      {formatNumber(displayValue)}
      {suffix}
    </>
  );
};

export default function OfflineProgressModal({
  stats,
  returnSequence,
  returnIdleSeconds,
  onClaimDaily,
  isRequesting,
}) {
  const [summary, setSummary] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDismissing, setIsDismissing] = useState(false);
  const [dailyClaimed, setDailyClaimed] = useState(false);
  const loadedForUser = useRef(null);
  const dialogRef = useRef(null);
  const continueButtonRef = useRef(null);
  const dismissingRef = useRef(false);
  const { addNotification } = useNotify();

  useEffect(() => {
    const storageKey = `welcomeBackShown:${stats.id}`;
    const loadKey = `${stats.id}:${returnSequence}`;

    if (
      loadedForUser.current === loadKey ||
      sessionStorage.getItem(storageKey) === "true"
    ) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setDailyClaimed(false);
    let isMounted = true;
    callBackend("/offline-progress")
      .then((data) => {
        if (!isMounted) return;
        loadedForUser.current = loadKey;
        setSummary(data.summary);
        setIsOpen(Boolean(data.summary) || returnSequence > 0);
      })
      .catch((error) => {
        if (isMounted) {
          addNotification(error.message, "error");
        }
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [addNotification, returnSequence, stats.canClaimDaily, stats.id]);

  const handleDailyClaim = async () => {
    const claimed = await onClaimDaily();
    if (claimed) setDailyClaimed(true);
  };

  const handleDismiss = useCallback(async () => {
    if (dismissingRef.current) return;
    dismissingRef.current = true;
    setIsDismissing(true);
    try {
      if (summary) {
        await callBackend("/offline-progress/dismiss", { method: "POST" });
      }
      sessionStorage.setItem(`welcomeBackShown:${stats.id}`, "true");
      setIsOpen(false);
    } catch (error) {
      addNotification(error.message, "error");
    } finally {
      dismissingRef.current = false;
      setIsDismissing(false);
    }
  }, [addNotification, stats.id, summary]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement;
    const focusContinueButton = requestAnimationFrame(() => {
      continueButtonRef.current?.focus();
    });
    const handleKeyDown = (event) => {
      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusableElements = Array.from(
        dialogRef.current.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusableElements.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      cancelAnimationFrame(focusContinueButton);
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [isOpen]);

  if (isLoading || !isOpen) return null;

  const metrics = summary
    ? [
        {
          label: "Net Cash",
          value: Math.abs(summary.cashDelta),
          prefix: summary.cashDelta < 0 ? "-$" : "+$",
          icon: Banknote,
          color: summary.cashDelta < 0 ? "text-red-400" : "text-green-400",
          show: summary.cashDelta !== 0,
        },
        {
          label: "Turns Restored",
          value: summary.turnsGained,
          prefix: "+",
          icon: Zap,
          color: "text-yellow-400",
          show: summary.turnsGained > 0,
        },
        {
          label: "XP Gained",
          value: summary.xpGained,
          prefix: "+",
          icon: TrendingUp,
          color: "text-blue-400",
          show: summary.xpGained > 0,
        },
        {
          label: "Product Made",
          value: summary.productsProduced,
          prefix: "+",
          icon: Boxes,
          color: "text-purple-400",
          show: summary.productsProduced > 0,
        },
        {
          label: "Product Moved",
          value: summary.productsSold,
          icon: PackageCheck,
          color: "text-cyan-400",
          show: summary.productsSold > 0,
        },
        {
          label: "Valuables Made",
          value: summary.valuablesProduced,
          prefix: "+",
          icon: Sparkles,
          color: "text-amber-300",
          show: summary.valuablesProduced > 0,
        },
      ].filter((metric) => metric.show)
    : [];
  const summaryAwaySeconds = summary?.awaySince
    ? Math.max(
        0,
        Math.floor((Date.now() - new Date(summary.awaySince).getTime()) / 1000),
      )
    : 0;
  const awaySeconds =
    Number(returnIdleSeconds) > 0
      ? Number(returnIdleSeconds)
      : summaryAwaySeconds;

  return (
    <div className="welcome-backdrop fixed inset-0 z-[10000] flex items-center justify-center bg-black/85 p-3 backdrop-blur-md sm:p-5">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="offline-progress-title"
        aria-describedby="offline-progress-description"
        tabIndex={-1}
        className="welcome-report relative max-h-[92vh] w-full max-w-3xl overflow-hidden border border-green-700 bg-zinc-950 font-mono shadow-[0_0_70px_rgba(34,197,94,0.24)]"
      >
        <div
          aria-hidden="true"
          className="welcome-scanline pointer-events-none absolute inset-x-0 top-0 z-20 h-px bg-green-300 shadow-[0_0_18px_4px_rgba(74,222,128,0.7)]"
        />
        <header className="relative overflow-hidden border-b border-green-900 bg-[linear-gradient(135deg,rgba(20,83,45,0.35),rgba(0,0,0,0.2))] p-6 md:p-8">
          <div
            aria-hidden="true"
            className="absolute right-5 top-5 hidden text-[9px] font-black uppercase tracking-[0.25em] text-green-900 sm:right-8 sm:block"
          >
            Secure uplink // active
          </div>
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.3em] text-green-500">
            Operations Report // Session Restored
          </p>
          <h2
            id="offline-progress-title"
            data-text="WELCOME BACK"
            className="welcome-glitch-title pr-8 text-3xl font-black uppercase italic tracking-tight text-white md:text-5xl"
          >
            Welcome Back
          </h2>
          <p
            id="offline-progress-description"
            className="mt-2 text-xs font-bold uppercase leading-relaxed text-zinc-500"
          >
            {summary || awaySeconds > 0
              ? "Your operation kept moving while you were away."
              : "Your connection to Trap City has been restored."}
          </p>
        </header>

        <div className="max-h-[calc(92vh-154px)] space-y-6 overflow-y-auto p-5 md:p-8">
          {awaySeconds > 0 && (
            <div className="flex flex-col justify-between gap-3 border border-green-900/70 bg-green-950/10 p-4 sm:flex-row sm:items-center">
              <div className="flex items-center gap-3">
                <Clock3 size={20} className="text-green-500" />
                <div>
                  <p className="text-[9px] font-black uppercase tracking-wider text-zinc-600">
                    Time Away
                  </p>
                  <p className="text-2xl font-black uppercase text-white">
                    {formatAwayDuration(awaySeconds)}
                  </p>
                </div>
              </div>
              {summary?.awaySince && (
                <div className="text-left sm:text-right">
                  <p className="text-[9px] font-black uppercase text-zinc-600">
                    Tracking Began
                  </p>
                  <p className="text-[10px] font-bold uppercase text-zinc-400">
                    {formatAwaySince(summary.awaySince)}
                  </p>
                </div>
              )}
            </div>
          )}

          {summary ? (
            <>
              <div className="flex items-center justify-between border border-zinc-800 bg-black/60 px-4 py-3">
                <p className="text-[9px] font-black uppercase tracking-wider text-zinc-600">
                  Engine Updates Processed
                </p>
                <p className="text-xl font-black text-green-400">
                  <AnimatedNumber value={summary.ticksProcessed} />
                </p>
              </div>

              {metrics.length > 0 ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {metrics.map((metric) => {
                    const Icon = metric.icon;
                    return (
                      <div
                        key={metric.label}
                        className="border border-zinc-800 bg-zinc-900/50 p-4"
                      >
                        <Icon size={20} className={metric.color} />
                        <p className="mt-3 text-[9px] font-black uppercase text-zinc-600">
                          {metric.label}
                        </p>
                        <p className={`mt-1 text-xl font-black ${metric.color}`}>
                          <AnimatedNumber
                            value={metric.value}
                            prefix={metric.prefix}
                          />
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="border border-zinc-800 bg-black/50 p-4 text-center text-[10px] font-bold uppercase leading-relaxed text-zinc-500">
                  Your operation remained steady during these engine updates.
                </p>
              )}

              {summary.raidsSuffered > 0 && (
                <div className="flex items-start gap-3 border border-red-900/70 bg-red-950/20 p-4">
                  <ShieldAlert size={20} className="shrink-0 text-red-500" />
                  <div>
                    <p className="text-xs font-black uppercase text-red-400">
                      Police Activity Recorded
                    </p>
                    <p className="mt-1 text-[10px] font-bold uppercase leading-relaxed text-red-300/70">
                      {summary.raidsSuffered} raid
                      {summary.raidsSuffered === 1 ? "" : "s"} occurred, with $
                      {formatNumber(summary.cashLost)} in recorded losses.
                    </p>
                  </div>
                </div>
              )}

              {summary.survivalIncidents > 0 && (
                <div className="flex items-start gap-3 border border-orange-900/70 bg-orange-950/20 p-4">
                  <ShieldAlert size={20} className="shrink-0 text-orange-500" />
                  <div>
                    <p className="text-xs font-black uppercase text-orange-400">
                      Survival Emergency
                    </p>
                    <p className="mt-1 text-[10px] font-bold uppercase leading-relaxed text-orange-300/70">
                      You collapsed
                      {summary.lastSurvivalReason
                        ? ` due to ${summary.lastSurvivalReason}`
                        : ""}{" "}
                      while away. Any cash impact is included in the net cash
                      total.
                    </p>
                  </div>
                </div>
              )}

              <p className="text-center text-[9px] font-bold uppercase leading-relaxed text-zinc-700">
                Unacknowledged offline periods remain combined in this report.
              </p>
            </>
          ) : awaySeconds > 0 ? (
            <p className="border border-zinc-800 bg-black/50 p-4 text-center text-[10px] font-bold uppercase leading-relaxed text-zinc-500">
              No engine updates completed during this sleep period.
            </p>
          ) : null}

          {stats.canClaimDaily && !dailyClaimed && (
            <section className="border border-yellow-800 bg-yellow-950/20 p-5">
              <p className="text-[10px] font-black uppercase tracking-widest text-yellow-500">
                Daily Package Available
              </p>
              <div className="mt-3 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <p className="text-xs font-bold uppercase text-zinc-400">
                  Standard reward: $1,000 and 15 turns
                </p>
                <button
                  type="button"
                  onClick={handleDailyClaim}
                  disabled={isRequesting}
                  className="bg-yellow-500 px-5 py-3 text-xs font-black uppercase text-black transition-colors hover:bg-yellow-400 disabled:cursor-wait disabled:bg-zinc-800 disabled:text-zinc-600"
                >
                  {isRequesting ? "Claiming..." : "Claim Daily"}
                </button>
              </div>
            </section>
          )}

          {dailyClaimed && (
            <p className="border border-green-900 bg-green-950/20 p-4 text-center text-[10px] font-black uppercase text-green-400">
              Bonus Claimed
            </p>
          )}

          <button
            ref={continueButtonRef}
            type="button"
            onClick={handleDismiss}
            disabled={isDismissing}
            className="w-full border border-green-500 bg-green-600 py-3 text-xs font-black uppercase tracking-widest text-black shadow-[0_0_20px_rgba(34,197,94,0.15)] transition-colors hover:bg-green-400 focus:outline-none focus:ring-2 focus:ring-green-300 focus:ring-offset-2 focus:ring-offset-black disabled:cursor-wait disabled:bg-zinc-800 disabled:text-zinc-600"
          >
            {isDismissing ? "Closing Report..." : "Enter the Streets"}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes welcome-backdrop-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes welcome-report-in {
          0% { opacity: 0; transform: scale(0.97) translateY(12px); clip-path: inset(0 0 100% 0); }
          55% { opacity: 1; clip-path: inset(0 0 0 0); }
          100% { transform: scale(1) translateY(0); }
        }
        @keyframes welcome-scan {
          0% { opacity: 0; transform: translateY(0); }
          15% { opacity: 1; }
          85% { opacity: 0.75; }
          100% { opacity: 0; transform: translateY(92vh); }
        }
        @keyframes welcome-glitch {
          0%, 100% { transform: translate(0); text-shadow: none; }
          20% { transform: translate(-1px, 1px); text-shadow: 2px 0 #22c55e, -2px 0 #14532d; }
          40% { transform: translate(1px, -1px); text-shadow: -2px 0 #22c55e, 2px 0 #ffffff; }
          60% { transform: translate(-1px, 0); text-shadow: 1px 0 #ffffff; }
          80% { transform: translate(1px, 1px); text-shadow: -1px 0 #22c55e; }
        }
        .welcome-backdrop { animation: welcome-backdrop-in 180ms ease-out both; }
        .welcome-report { animation: welcome-report-in 380ms cubic-bezier(0.16, 1, 0.3, 1) both; }
        .welcome-scanline { animation: welcome-scan 900ms ease-out both; }
        .welcome-glitch-title { animation: welcome-glitch 480ms steps(2, end) 1 180ms; }
        @media (prefers-reduced-motion: reduce) {
          .welcome-backdrop,
          .welcome-report,
          .welcome-scanline,
          .welcome-glitch-title {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
